import React, { useId } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    label?: string;
    error?: string;
    options: SelectOption[];
    placeholder?: string;
    fullWidth?: boolean;
    fieldSize?: 'sm' | 'md' | 'lg';
}

const heights = { sm: 'h-11', md: 'h-[50px]', lg: 'h-14' };

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ label, error, options, placeholder, fullWidth = true, fieldSize = 'md', className, id, ...props }, ref) => {
    const autoId = useId();
    const selectId = id || `sel-${autoId}`;
    return (
        <div className={clsx('flex flex-col gap-2', fullWidth && 'w-full')}>
            {label && (
                <label htmlFor={selectId} className="text-[13px] font-extrabold text-ink-secondary">
                    {label}
                    {props.required && <span className="ml-0.5 text-status-danger">*</span>}
                </label>
            )}
            <div className={clsx('relative flex items-center rounded-md border bg-surface-card transition-[border-color,box-shadow] focus-within:border-2 focus-within:border-accent-500 focus-within:shadow-focus', heights[fieldSize], error ? 'border-status-danger' : 'border-line')}>
                <select
                    ref={ref}
                    id={selectId}
                    aria-invalid={!!error}
                    className={clsx('h-full w-full cursor-pointer appearance-none bg-transparent pl-[18px] pr-10 text-base font-semibold text-ink-primary outline-none', className)}
                    {...props}
                >
                    {placeholder && <option value="" disabled>{placeholder}</option>}
                    {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-4 w-[18px] h-[18px] text-ink-muted" />
            </div>
            {error && <p className="text-xs font-semibold text-status-danger">{error}</p>}
        </div>
    );
});
Select.displayName = 'Select';
export default Select;
